import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '~/redux/hooks';
import { resolveConnectJanusErrorMessage } from '~/hooks/janus/wsError';
import { useJanusCall } from '~/hooks/useJanusCall';
import { callAPI, CallApiError, CallNetworkError } from '~/services/callAPI';
import { notifyCall } from '~/utils/helper';
import {
  isCalleeDeviceBusy,
  isCallerWebBusy,
  isPollStatusCalleeBusy,
} from '~/utils/callBusy';
import type { IPerson } from '~/utils/interface/person';

export type CallStatus =
  | 'idle'
  | 'initiating'
  | 'pending'
  | 'accepted'
  | 'calling'
  | 'oncall'
  | 'rejected'
  | 'missed'
  | 'ended';

export interface CallData {
  username?: string;
  display_name?: string;
  icon?: string;
  imei?: string;
  current_address?: string;
  room_id?: string | null;
  wss_url?: string | null;
  token?: string | null;
}

export interface CallContextType {
  callStatus: CallStatus;
  callId: string | null;
  callData: CallData | null;
  isCallDeviceModalOpen: boolean;
  isActiveCallVisible: boolean;
  callDuration: number;
  isLoading: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  openCallDeviceModal: (person: IPerson) => void;
  startOutgoingCall: () => Promise<void>;
  closeAllAndHangup: () => Promise<void>;
  setMicMuted: (muted: boolean) => void;
  
  callerUsername: string | undefined;
  
  micUnavailable: boolean;
  
  speakerUnavailable: boolean;
  reportRemotePlaybackFailure: () => void;
  reportSpeakerRecovered: () => void;
  registerRemotePlaybackRecover: (fn: (() => Promise<boolean>) | null) => void;
}

const CallContext = createContext<CallContextType | null>(null);

const DEFAULT_WEB_JANUS_USERNAME = 'anhhieubs';
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30;

const LIVE_CALL_STATUSES: CallStatus[] = ['accepted', 'calling', 'oncall'];

const OUTBOUND_CALL_STATUSES: CallStatus[] = [
  'initiating',
  'pending',
  'accepted',
  'calling',
  'oncall',
];

function shouldNotifyBackendEnd(status: CallStatus, callId: string | null): callId is string {
  return Boolean(callId) && OUTBOUND_CALL_STATUSES.includes(status);
}

function resolveCallTargetUsername(person: IPerson): string | undefined {
  const target =
    person.username?.trim() ||
    person.cccd?.trim() ||
    person.devname?.trim() ||
    person.imei?.trim();
  return target || undefined;
}

function personToCallData(person: IPerson): CallData {
  return {
    username: resolveCallTargetUsername(person),
    display_name: person.full_name || person.imei,
    icon: person.icon,
    imei: person.imei,
    current_address: person.current_address,
  };
}

function isTemporaryNetworkError(err: unknown): boolean {
  if (err instanceof CallNetworkError) return true;
  if (err instanceof AxiosError) {
    if (!err.response) return true;
    const code = err.code ?? '';
    if (/ERR_NETWORK|ERR_NAME_NOT_RESOLVED|ERR_INTERNET|ECONNABORTED|ETIMEDOUT/i.test(code)) {
      return true;
    }
  }
  if (err instanceof Error) {
    return /ERR_NAME_NOT_RESOLVED|ERR_NETWORK|ERR_INTERNET_DISCONNECTED|Network Error|Failed to fetch|timeout|ECONNABORTED/i.test(
      err.message
    );
  }
  return false;
}

function isCallStatusResourceGoneError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (isTemporaryNetworkError(err)) return false;
  if (err instanceof AxiosError && err.response?.status === 404) return true;
  const m = err.message;
  return /\b404\b|NOT_FOUND|không tồn tại|đã kết thúc|Cuộc gọi không|no longer exists|not found/i.test(m);
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('admin/call');
  const { userProfile } = useAppSelector((state) => state.auth);

  const webJanusUsername =
    userProfile?.username?.trim() ||
    process.env.REACT_APP_JANUS_WEB_USERNAME ||
    DEFAULT_WEB_JANUS_USERNAME;

  const callerUsername = userProfile?.username?.trim() || undefined;

  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [callId, setCallId] = useState<string | null>(null);
  const [callData, setCallData] = useState<CallData | null>(null);
  const [isCallDeviceModalOpen, setIsCallDeviceModalOpen] = useState(false);
  const [isActiveCallVisible, setIsActiveCallVisible] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [micUnavailable, setMicUnavailable] = useState(false);
  const [speakerUnavailable, setSpeakerUnavailable] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptsRef = useRef(0);
  const callDataRef = useRef<CallData | null>(null);
  const callIdRef = useRef<string | null>(null);
  const callStatusRef = useRef<CallStatus>('idle');
  const prevJanusStatusRef = useRef<string>('idle');
  const durationStartedRef = useRef(false);
  const isUserInitiatedHangupRef = useRef(false);
  const micUnavailableRef = useRef(false);
  const speakerUnavailableRef = useRef(false);
  const remotePlaybackRecoverRef = useRef<(() => Promise<boolean>) | null>(null);
  const onMediaConnectionLostRef = useRef<() => void>(() => undefined);
  const onNetworkReconnectFailedRef = useRef<() => void>(() => undefined);
  const onRemoteCallEndedRef = useRef<() => void>(() => undefined);
  const mediaReconnectInFlightRef = useRef(false);
  const recoverStuckConnectingRef = useRef(false);
  const resetCallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const translateCall = useCallback((key: string) => t(key), [t]);

  useEffect(() => {
    micUnavailableRef.current = micUnavailable;
  }, [micUnavailable]);

  useEffect(() => {
    speakerUnavailableRef.current = speakerUnavailable;
  }, [speakerUnavailable]);

  const clearAudioUnavailableState = useCallback(() => {
    setMicUnavailable(false);
    setSpeakerUnavailable(false);
  }, []);

  const handleAudioHardwareLost = useCallback(
    (detail: { target: 'mic' | 'speaker'; reason: 'unplugged' | 'not_found' | 'playback' }) => {
      if (!LIVE_CALL_STATUSES.includes(callStatusRef.current)) return;
      if (detail.target === 'mic') {
        if (micUnavailableRef.current) return;
        setMicUnavailable(true);
      } else {
        if (speakerUnavailableRef.current) return;
        setSpeakerUnavailable(true);
      }
    },
    []
  );

  const reportRemotePlaybackFailure = useCallback(() => {
    handleAudioHardwareLost({ target: 'speaker', reason: 'playback' });
  }, [handleAudioHardwareLost]);

  const reportSpeakerRecovered = useCallback(() => {
    setSpeakerUnavailable(false);
  }, []);

  const registerRemotePlaybackRecover = useCallback((fn: (() => Promise<boolean>) | null) => {
    remotePlaybackRecoverRef.current = fn;
  }, []);

  const {
    ensureSession,
    joinRoom,
    setMuted,
    hangup: janusHangup,
    reconnectMedia,
    localStream,
    remoteStream,
    callStatus: janusStatus,
    error: janusError,
  } = useJanusCall({
    cleanupOnUnmount: true,
    remotePlaybackRecoverRef,
    onAudioHardwareLost: handleAudioHardwareLost,
    onLocalAudioRecovered: () => {
      setMicUnavailable(false);
      setMuted(false);
    },
    onRemotePlaybackRecovered: () => {
      setSpeakerUnavailable(false);
    },
    onMediaConnectionLost: () => onMediaConnectionLostRef.current(),
    onRemoteCallEnded: () => onRemoteCallEndedRef.current(),
    onNetworkReconnectFailed: () => onNetworkReconnectFailedRef.current(),
    translateCall,
  });

  const tryReconnectCallMedia = useCallback(async () => {
    if (mediaReconnectInFlightRef.current) return;
    if (callStatusRef.current !== 'oncall') {
      return;
    }

    mediaReconnectInFlightRef.current = true;
    try {
      notifyCall('info', t('toast.reconnectingMedia'), { toastId: 'call-media-reconnect' });
      await reconnectMedia();
      notifyCall('success', t('toast.mediaReconnected'), { toastId: 'call-media-reconnected' });
    } catch {
      notifyCall('warning', t('toast.mediaReconnectFailed'), { toastId: 'call-media-reconnect-failed' });
    } finally {
      mediaReconnectInFlightRef.current = false;
    }
  }, [reconnectMedia, t]);

  useEffect(() => {
    onMediaConnectionLostRef.current = () => {
      void tryReconnectCallMedia();
    };
  }, [tryReconnectCallMedia]);

  useEffect(() => {
    callDataRef.current = callData;
  }, [callData]);

  useEffect(() => {
    callIdRef.current = callId;
  }, [callId]);

  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    pollAttemptsRef.current = 0;
  }, []);

  const stopDurationCounter = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    durationStartedRef.current = false;
  }, []);

  const startDurationCounter = useCallback(() => {
    stopDurationCounter();
    durationStartedRef.current = true;
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, [stopDurationCounter]);

  const resetCall = useCallback(() => {
    stopPolling();
    stopDurationCounter();
    setCallStatus('idle');
    setCallId(null);
    setCallData(null);
    setCallDuration(0);
    setIsCallDeviceModalOpen(false);
    setIsActiveCallVisible(false);
    isUserInitiatedHangupRef.current = false;
    clearAudioUnavailableState();
  }, [clearAudioUnavailableState, stopPolling, stopDurationCounter]);

  const cancelScheduledResetCall = useCallback(() => {
    if (resetCallTimerRef.current) {
      clearTimeout(resetCallTimerRef.current);
      resetCallTimerRef.current = null;
    }
  }, []);

  const scheduleResetCall = useCallback(
    (delayMs: number) => {
      cancelScheduledResetCall();
      resetCallTimerRef.current = setTimeout(() => {
        resetCallTimerRef.current = null;
        resetCall();
      }, delayMs);
    },
    [cancelScheduledResetCall, resetCall]
  );

  const dismissCallUi = useCallback(() => {
    setIsActiveCallVisible(false);
    setIsCallDeviceModalOpen(false);
  }, []);

  const handleRemoteCallEnded = useCallback(() => {
    const status = callStatusRef.current;
    if (!LIVE_CALL_STATUSES.includes(status)) return;

    isUserInitiatedHangupRef.current = false;
    stopPolling();
    stopDurationCounter();
    dismissCallUi();
    setCallStatus('ended');
    notifyCall('error', t('toast.ended'), { toastId: 'call-remote-ended' });
    scheduleResetCall(2000);
  }, [dismissCallUi, scheduleResetCall, stopDurationCounter, stopPolling, t]);

  useEffect(() => {
    onRemoteCallEndedRef.current = handleRemoteCallEnded;
  }, [handleRemoteCallEnded]);

  const reportCallError = useCallback(
    (message: string, options?: { status?: CallStatus; resetAfterMs?: number }) => {
      notifyCall('error', message, { toastId: 'call-report-error' });
      stopPolling();
      stopDurationCounter();
      dismissCallUi();
      if (options?.status) {
        setCallStatus(options.status);
      }
      if (options?.resetAfterMs !== undefined) {
        scheduleResetCall(options.resetAfterMs);
      }
    },
    [dismissCallUi, scheduleResetCall, stopDurationCounter, stopPolling]
  );

  useEffect(() => {
    if (janusStatus === 'oncall') {
      setCallStatus('oncall');
      if (!durationStartedRef.current) {
        startDurationCounter();
      }
      return;
    }
    durationStartedRef.current = false;
  }, [janusStatus, startDurationCounter]);

  useEffect(() => {
    if (!janusError) return;
    reportCallError(janusError, { status: 'ended', resetAfterMs: 2000 });
  }, [janusError, reportCallError]);

  useEffect(() => {
    const prevJanus = prevJanusStatusRef.current;
    prevJanusStatusRef.current = janusStatus;

    if (janusStatus !== 'idle' && janusStatus !== 'ready') return;
    if (prevJanus !== 'oncall' && prevJanus !== 'joining') return;

    const status = callStatusRef.current;
    if (!['calling', 'oncall', 'accepted'].includes(status)) return;

    stopPolling();
    stopDurationCounter();
    dismissCallUi();
    setCallStatus('ended');
    scheduleResetCall(2000);
  }, [dismissCallUi, janusStatus, scheduleResetCall, stopDurationCounter, stopPolling]);

 
  const connectJanusMedia = useCallback(
    async (media: { wss_url?: string | null; room_id?: string | null; token?: string | null }) => {
      const roomId = media.room_id;
      if (!roomId) {
        throw new Error(t('errors.joinNoRoomId'));
      }

      const wssUrl = media.wss_url || undefined;
      await ensureSession(wssUrl);
      await joinRoom({
        room: roomId,
        display: webJanusUsername,
        token: media.token || undefined,
      });
    },
    [ensureSession, joinRoom, t, webJanusUsername]
  );

  useEffect(() => {
    if (callStatus !== 'calling') {
      recoverStuckConnectingRef.current = false;
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (callStatusRef.current !== 'calling') return;
      if (recoverStuckConnectingRef.current) return;
      recoverStuckConnectingRef.current = true;

      const roomId = callDataRef.current?.room_id;
      if (!roomId) return;

      notifyCall('warning', t('toast.reconnectingMedia'), { toastId: 'call-stuck-connecting' });
      void connectJanusMedia({
        wss_url: callDataRef.current?.wss_url,
        room_id: roomId,
      }).catch((err) => {
        reportCallError(resolveConnectJanusErrorMessage(err, t), {
          status: 'ended',
          resetAfterMs: 2000,
        });
      });
    }, 22_000);

    return () => window.clearTimeout(timer);
  }, [callStatus, connectJanusMedia, reportCallError, t]);

  const openCallDeviceModal = useCallback(
    (person: IPerson) => {
      cancelScheduledResetCall();

      const mappedData = personToCallData(person);

      if (isCallerWebBusy(callStatusRef.current, isActiveCallVisible)) {
        notifyCall('warning', t('toast.callerBusy'), { toastId: 'call-caller-busy' });
        return;
      }

      const staleStatus = callStatusRef.current;
      if (staleStatus !== 'idle') {
        stopPolling();
        stopDurationCounter();
        void janusHangup();
        setCallStatus('idle');
        setCallId(null);
        setIsActiveCallVisible(false);
        isUserInitiatedHangupRef.current = false;
        clearAudioUnavailableState();
      }

      setCallData(mappedData);
      setIsCallDeviceModalOpen(true);
      clearAudioUnavailableState();
    },
    [
      cancelScheduledResetCall,
      clearAudioUnavailableState,
      isActiveCallVisible,
      janusHangup,
      stopDurationCounter,
      stopPolling,
      t,
    ]
  );

  const startOutgoingCall = useCallback(async () => {
    const target = callDataRef.current?.username?.trim();
    if (!target) {
      notifyCall('error', t('errors.callTargetMissing'), { toastId: 'call-target-missing' });
      return;
    }

    if (isCallerWebBusy(callStatusRef.current, isActiveCallVisible)) {
      notifyCall('warning', t('toast.callerBusy'), { toastId: 'call-caller-busy' });
      return;
    }

    try {
      setIsLoading(true);

      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
        probe.getTracks().forEach((track) => track.stop());
        clearAudioUnavailableState();
      } catch {
        setMicUnavailable(true);
      }

      setCallStatus('initiating');
      setIsCallDeviceModalOpen(false);

      const initiateResponse = await callAPI.initiateCall(target);

      if (
        isCalleeDeviceBusy({
          messageCode: initiateResponse.message_code,
          message: initiateResponse.message,
          status: initiateResponse.status,
        })
      ) {
        reportCallError(t('toast.calleeBusy'), { status: 'rejected', resetAfterMs: 2000 });
        return;
      }

      setCallId(initiateResponse.call_id);
      callIdRef.current = initiateResponse.call_id;
      setCallStatus('pending');
      setIsActiveCallVisible(true);

      pollAttemptsRef.current = 0;

      const poll = async () => {
        pollAttemptsRef.current += 1;
        const id = callIdRef.current;
        if (!id) return;

        if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
          reportCallError(t('toast.noAnswer'), { status: 'missed' });
          return;
        }

        try {
          const status = await callAPI.getCallStatus(id);

          if (status.status === 'accepted') {
            stopPolling();
            setCallStatus('accepted');
            setCallData((prev) => ({
              ...prev,
              room_id: status.room_id,
              wss_url: status.wss_url,
            }));

            if (!status.room_id) {
              reportCallError(t('toast.roomIdMissing'), { status: 'ended' });
              return;
            }

            try {
              setCallStatus('calling');
              await connectJanusMedia({
                wss_url: status.wss_url,
                room_id: status.room_id,
              });
            } catch (err) {
              const errMsg = err instanceof Error ? err.message : String(err);
              const isMicFailure =
                /micro|NotFoundError|microphone|Permission|Requested device|NotReadableError/i.test(
                  errMsg
                );
              if (isMicFailure) {
                setMicUnavailable(true);
                handleAudioHardwareLost({ target: 'mic', reason: 'not_found' });
              } else {
                reportCallError(resolveConnectJanusErrorMessage(err, t), {
                  status: 'ended',
                  resetAfterMs: 2000,
                });
              }
            }
            return;
          }

          if (
            isPollStatusCalleeBusy(status.status, {
              messageCode: status.message_code,
              message: status.message,
            })
          ) {
            stopPolling();
            reportCallError(t('toast.calleeBusy'), { status: 'rejected' });
            return;
          }

          if (['rejected', 'missed', 'ended'].includes(status.status)) {
            stopPolling();
            const messages: Record<string, string> = {
              rejected: t('toast.rejected'),
              missed: t('toast.missed'),
              ended: t('toast.ended'),
            };
            reportCallError(messages[status.status] || t('toast.endedGeneric'), {
              status: status.status as CallStatus,
            });
          }
        } catch (err: unknown) {
          if (
            err instanceof CallApiError &&
            isCalleeDeviceBusy({
              messageCode: err.messageCode,
              message: err.message,
              httpStatus: err.httpStatus,
            })
          ) {
            stopPolling();
            reportCallError(t('toast.calleeBusy'), { status: 'rejected' });
            return;
          }
          if (isCallStatusResourceGoneError(err)) {
            reportCallError(t('toast.callRejectedOrGone'), { status: 'rejected' });
            return;
          }
          if (isTemporaryNetworkError(err)) {
            notifyCall('warning', t('toast.pollingNetworkLost'), { toastId: 'call-polling-network' });
          }
        }
      };

      pollingRef.current = setInterval(() => {
        void poll();
      }, POLL_INTERVAL_MS);
      await poll();
    } catch (err) {
      if (
        err instanceof CallApiError &&
        isCalleeDeviceBusy({
          messageCode: err.messageCode,
          message: err.message,
          httpStatus: err.httpStatus,
        })
      ) {
        reportCallError(t('toast.calleeBusy'), { status: 'rejected', resetAfterMs: 2000 });
        return;
      }
      const message =
        err instanceof Error ? err.message : t('toast.initFailedUnknown');
      reportCallError(message, { status: 'idle' });
    } finally {
      setIsLoading(false);
    }
  }, 
 // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    clearAudioUnavailableState,
    connectJanusMedia,
    handleAudioHardwareLost,
    reportCallError,
    resetCall,
    stopPolling,
    isActiveCallVisible,
    stopDurationCounter,
    t,
  ]);

  const closeAllAndHangup = useCallback(async () => {
    isUserInitiatedHangupRef.current = true;

    const id = callIdRef.current;
    const status = callStatusRef.current;
    const hadActiveCall = OUTBOUND_CALL_STATUSES.includes(status);
    const notifyBackend = shouldNotifyBackendEnd(status, id);

    stopPolling();
    stopDurationCounter();
    janusHangup();

    if (notifyBackend) {
      try {
        const result = await callAPI.endCall(id);
        setCallDuration(result.duration_seconds);
      } catch (err) {
        console.warn('[call] endCall failed while hanging up', err);
      }
    }

    setCallStatus('ended');
    dismissCallUi();
    if (hadActiveCall) {
      notifyCall('error', t('toast.ended'), { toastId: 'call-user-ended' });
    }
    scheduleResetCall(2000);
  }, [dismissCallUi, janusHangup, scheduleResetCall, stopDurationCounter, stopPolling, t]);

  useEffect(() => {
    onNetworkReconnectFailedRef.current = () => {
      if (!LIVE_CALL_STATUSES.includes(callStatusRef.current)) return;
      notifyCall('warning', t('toast.networkReconnectTimeout'), { toastId: 'call-network-timeout' });
      void closeAllAndHangup();
    };
  }, [closeAllAndHangup, t]);

  const setMicMuted = useCallback(
    (muted: boolean) => {
      if (micUnavailable && !muted) return;
      setMuted(muted);
    },
    [micUnavailable, setMuted]
  );

  useEffect(() => {
    return () => {
      stopPolling();
      stopDurationCounter();
      cancelScheduledResetCall();
    };
  }, [cancelScheduledResetCall, stopPolling, stopDurationCounter]);

  const value = useMemo<CallContextType>(
    () => ({
      callStatus,
      callId,
      callData,
      isCallDeviceModalOpen,
      isActiveCallVisible,
      callDuration,
      isLoading,
      localStream,
      remoteStream,
      openCallDeviceModal,
      startOutgoingCall,
      closeAllAndHangup,
      setMicMuted,
      callerUsername,
      micUnavailable,
      speakerUnavailable,
      reportRemotePlaybackFailure,
      reportSpeakerRecovered,
      registerRemotePlaybackRecover,
    }),
    [
      callStatus,
      callId,
      callData,
      isCallDeviceModalOpen,
      isActiveCallVisible,
      callDuration,
      isLoading,
      localStream,
      remoteStream,
      openCallDeviceModal,
      startOutgoingCall,
      closeAllAndHangup,
      setMicMuted,
      callerUsername,
      micUnavailable,
      speakerUnavailable,
      reportRemotePlaybackFailure,
      reportSpeakerRecovered,
      registerRemotePlaybackRecover,
    ]
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCallContext(): CallContextType {
  const ctx = useContext(CallContext);
  if (!ctx) {
    throw new Error('useCallContext must be used within CallProvider');
  }
  return ctx;
}
